/**
 * `reddit` — a subreddit listing or a user's posts through Reddit's Atom feeds (sources.md "reddit").
 * No CORS → `relay-only`. The JSON API answers 403 without OAuth, so the feed is the only public path.
 * Body: Reddit's wrapper table and "submitted by" footer are removed; what remains is the self-text, the
 * preview image and the outbound link.
 */

import type { RawItem, RedditConfig, SourceClient, SourceFetchContext, SourceFetchResult } from '../contracts/sources.js';
import { firstImageUrl } from '../kernel/html.js';
import { entryToItem, parseFeed } from './feed.js';
import { assertOk, contextConditionalHeaders, escapeHtml, invalidInput, newestFirst, parseHttpUrl, parseRetryAfter, rateLimited, validatorsOf } from './http.js';

export const REDDIT_ORIGIN = 'https://www.reddit.com';
const LISTINGS: readonly RedditConfig['listing'][] = ['new', 'hot', 'top'];
const NAME = /^[A-Za-z0-9_-]{2,32}$/;

export function parseRedditAddress(address: string): { mode: RedditConfig['mode']; name: string; listing?: RedditConfig['listing'] } | null {
  const raw = address.trim();
  const short = /^\/?(r|u|user)\/([A-Za-z0-9_-]+)\/?$/i.exec(raw);
  if (short?.[1] && short[2]) return { mode: short[1].toLowerCase() === 'r' ? 'subreddit' : 'user', name: short[2] };
  const url = parseHttpUrl(raw);
  if (url && /(^|\.)reddit\.com$/i.test(url.hostname)) {
    const parts = url.pathname.split('/').filter((p) => p !== '');
    if (parts[0] === 'r' && parts[1]) {
      const listing = LISTINGS.find((l) => l === parts[2]);
      return { mode: 'subreddit', name: parts[1], ...(listing ? { listing } : {}) };
    }
    if ((parts[0] === 'u' || parts[0] === 'user') && parts[1]) return { mode: 'user', name: parts[1] };
    return null;
  }
  return null;
}

export function redditFeedUrl(c: Pick<RedditConfig, 'mode' | 'name' | 'listing'>): string {
  return c.mode === 'user' ? `${REDDIT_ORIGIN}/user/${encodeURIComponent(c.name)}/.rss` : `${REDDIT_ORIGIN}/r/${encodeURIComponent(c.name)}/${c.listing}/.rss?limit=50`;
}

function decodeAttr(s: string): string {
  return s.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

/** Remove Reddit's wrapper table and "submitted by" footer; keep self-text, preview image and outbound link. */
export function cleanRedditHtml(html: string, permalink: string | undefined, title: string | undefined): { html: string; image: string | null } {
  const self = /<!-- SC_OFF -->([\s\S]*?)<!-- SC_ON -->/.exec(html)?.[1]?.trim() ?? '';
  const link = /<a href="([^"]+)">\[link\]<\/a>/.exec(html)?.[1];
  const outbound = link ? decodeAttr(link) : null;
  const tableImage = /<table>[\s\S]*?<\/table>/.test(html) ? firstImageUrl(/<table>([\s\S]*?)<\/table>/.exec(html)?.[1] ?? '') : null;
  const parts: string[] = [];
  if (self) parts.push(self);
  if (tableImage) parts.push(`<p><img src="${escapeHtml(tableImage)}" alt="${escapeHtml(title ?? '')}"></p>`);
  if (outbound && outbound !== permalink && /^https?:\/\//i.test(outbound) && !(tableImage && /^https?:\/\/i\.redd\.it\//i.test(outbound))) {
    parts.push(`<p><a href="${escapeHtml(outbound)}">${escapeHtml(outbound)}</a></p>`);
  }
  return { html: parts.join(''), image: tableImage };
}

export function redditItems(xml: string): RawItem[] {
  const feed = parseFeed(xml, 'The Reddit feed');
  const items: RawItem[] = [];
  for (const entry of feed.entries) {
    const item = entryToItem(entry, REDDIT_ORIGIN);
    if (!item) continue;
    if (item.author) item.author = item.author.replace(/^\/?u\//, '');
    const cleaned = cleanRedditHtml(item.html ?? '', item.url, item.title);
    if (cleaned.html) item.html = cleaned.html;
    else delete item.html;
    if (!item.imageUrl && cleaned.image) item.imageUrl = cleaned.image;
    items.push(item);
  }
  return items;
}

export const redditClient: SourceClient<'reddit'> = {
  kind: 'reddit',
  defaultPullMinutes: 30,

  async resolve(input) {
    const parsed = input.config?.name && input.config.mode ? { mode: input.config.mode, name: input.config.name } : parseRedditAddress(input.address);
    if (!parsed || !NAME.test(parsed.name)) throw invalidInput('Enter r/name, u/name or a Reddit URL.');
    const listing = input.config?.listing ?? ('listing' in parsed && parsed.listing ? parsed.listing : 'new');
    const lower = parsed.name.toLowerCase();
    if (parsed.mode === 'user') {
      return { kind: 'reddit', locator: `reddit:u:${lower}`, name: `u/${parsed.name}`, config: { kind: 'reddit', mode: 'user', name: parsed.name, listing }, requiredSecrets: [] };
    }
    return { kind: 'reddit', locator: `reddit:r:${lower}:${listing}`, name: `r/${parsed.name}`, config: { kind: 'reddit', mode: 'subreddit', name: parsed.name, listing }, requiredSecrets: [] };
  },

  async fetch(ctx: SourceFetchContext<'reddit'>): Promise<SourceFetchResult> {
    const url = redditFeedUrl(ctx.source.config);
    const response = await ctx.runtime.fetcher.fetch({ url, method: 'GET', headers: { Accept: 'application/atom+xml, application/xml;q=0.9, */*;q=0.8', ...contextConditionalHeaders(ctx) }, route: 'relay-only' });
    const now = ctx.runtime.clock.now();
    if (response.status === 429) {
      // Reddit names the wait in x-ratelimit-reset (seconds) when Retry-After is absent.
      const reset = Number(response.headers['x-ratelimit-reset']);
      throw rateLimited('Reddit asked us to slow down (HTTP 429).', parseRetryAfter(response.headers['retry-after'], now) ?? (Number.isFinite(reset) ? reset * 1000 : null), { status: 429 });
    }
    assertOk(response, { credential: false, what: ctx.source.name || url, now });
    const cursor = ctx.cursor ?? {};
    if (response.status === 304) return { items: [], cursor, notModified: true };
    const items = newestFirst(redditItems(response.text), ctx.maxItems);
    const newest = items[0];
    return { items, cursor: newest ? { ...cursor, newestId: newest.externalId } : cursor, ...validatorsOf(response) };
  },
};
